import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { clientPath, useFollowSwitcher } from '../lib/clientLink.js';
import { renderMd, fetchPackageText } from '../lib/markdown.js';

// Session 5 Part C: agency review of a client's baseline analysis.
// Agency-only RPCs: portal_approve_strategy / portal_rerun_analysis.

export default function AdminAnalysis() {
  const { clientId } = useParams();
  const { isAgency, activeId, setActiveId } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (clientId && clientId !== activeId) setActiveId(clientId); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  useFollowSwitcher(clientId, (id) => `/admin/clients/${id}/analysis`);   // Session 17
  const [job, setJob] = useState(null);
  const [docs, setDocs] = useState({ baseline: null, strategy: null });
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [tab, setTab] = useState('baseline');

  const load = useCallback(async () => {
    setError(null);
    const { data, error: err } = await supabase
      .from('v_portal_baselines').select('*').eq('client_id', clientId)
      .neq('status', 'flagged').order('created_at', { ascending: false }).limit(1);
    if (err) { setError(err.message); return; }
    const j = data?.[0] || null;
    setJob(j);
    if (j?.baseline_path) {
      const [b, s] = await Promise.all([
        fetchPackageText(supabase, j.baseline_path).catch((e) => `_Couldn't load: ${e.message}_`),
        j.strategy_path ? fetchPackageText(supabase, j.strategy_path).catch((e) => `_Couldn't load: ${e.message}_`) : '',
      ]);
      setDocs({ baseline: b, strategy: s });
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  if (!isAgency) return <Navigate to="/" replace />;
  if (error) return <div className="empty"><h2>Couldn't load the analysis</h2><p>{error}</p></div>;
  if (job === null) return <p className="lede">Loading…</p>;
  if (!job) return (
    <div className="empty">
      <h2>No analysis yet</h2>
      <p><Link to={clientPath(`/admin/clients/${clientId}/onboarding`, clientId)}>Back to onboarding</Link></p>
    </div>
  );

  async function approve() {
    setBusy(true); setBanner(null);
    const { error: err } = await supabase.rpc('portal_approve_strategy', { p_job_id: job.job_id });
    setBusy(false);
    if (err) { setBanner({ tone: 'err', text: err.message }); return; }
    setBanner({ tone: 'ok', text: 'Strategy approved. The first quarterly plan is being prepared.' });
    setTimeout(() => navigate(clientPath(`/admin/clients/${clientId}/onboarding`, clientId)), 1200);
  }

  async function rerun() {
    if (!notes.trim()) { setBanner({ tone: 'err', text: 'Add a note on what to change first.' }); return; }
    setBusy(true); setBanner(null);
    const { error: err } = await supabase.rpc('portal_rerun_analysis', { p_job_id: job.job_id, p_notes: notes.trim() });
    setBusy(false);
    if (err) { setBanner({ tone: 'err', text: err.message }); return; }
    setBanner({ tone: 'ok', text: 'Re-running with your notes. It usually takes 10 to 20 minutes.' });
    setTimeout(() => navigate(clientPath(`/admin/clients/${clientId}/onboarding`, clientId)), 1200);
  }

  const reviewable = job.status === 'agency_review';

  return (
    <>
      <p><Link to={clientPath(`/admin/clients/${clientId}/onboarding`, clientId)}>← Onboarding</Link></p>
      <h1>Baseline analysis: {job.client_name}</h1>
      <p className="lede">
        {job.status === 'approved' ? 'Approved.' : reviewable ? 'Ready for your review.' : `Status: ${job.status}.`}
        {job.notes ? ` Run with your notes: "${job.notes}".` : ''}
      </p>
      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}

      {Array.isArray(job.summary) && job.summary.length > 0 && (
        <section className="review-section">
          <h2>Summary</h2>
          <ul>{job.summary.map((l, i) => <li key={i}>{l}</li>)}</ul>
        </section>
      )}

      <div className="review-actions" role="tablist">
        <button type="button" className={`btn ${tab === 'baseline' ? 'btn-primary' : ''}`} onClick={() => setTab('baseline')}>Full findings</button>
        <button type="button" className={`btn ${tab === 'strategy' ? 'btn-primary' : ''}`} onClick={() => setTab('strategy')}>Client strategy preview</button>
      </div>
      <article className="review-section md-doc strategy-doc"
               dangerouslySetInnerHTML={{ __html: renderMd((tab === 'baseline' ? docs.baseline : docs.strategy) || '_Loading…_') }} />

      {reviewable && (
        <section className="review-section">
          <h2>Decide</h2>
          <div className="review-actions">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={approve}>Approve strategy</button>
          </div>
          <label className="field" style={{ marginTop: 16 }}>
            <span className="field-label">Re-run with notes</span>
            <textarea className="review-textarea" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="What should the next run do differently?" />
          </label>
          <div className="review-actions">
            <button type="button" className="btn" disabled={busy} onClick={rerun}>Re-run with notes</button>
          </div>
        </section>
      )}
    </>
  );
}
